"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDaysOfMonthToProcess = exports.getFollowingMonthOccurrence = exports.getNextMonthlyOccurrenceInTimezone = exports.getNextMonthlyOccurrence = void 0;
const MAX_DAY_OF_MONTH = 31;
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const clampDayToMonth = (dayOfMonth, year, month) => Math.min(dayOfMonth, getLastDayOfMonth(year, month));
/**
 * Returns the next occurrence of a monthly recurring event for a given dayOfMonth.
 * Clamps to the last day of the month for months with fewer days
 * (e.g. dayOfMonth=31 in February returns Feb 28).
 */
const getNextMonthlyOccurrence = (dayOfMonth) => {
    const today = new Date();
    // Try this month, clamping to last valid day
    const lastDayThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), Math.min(dayOfMonth, lastDayThisMonth));
    if (thisMonth > today) {
        return thisMonth;
    }
    // Next month
    const nextMonth = today.getMonth() + 1;
    const nextYear = nextMonth > 11 ? today.getFullYear() + 1 : today.getFullYear();
    const normalizedMonth = nextMonth > 11 ? 0 : nextMonth;
    const lastDayNextMonth = new Date(nextYear, normalizedMonth + 1, 0).getDate();
    return new Date(nextYear, normalizedMonth, Math.min(dayOfMonth, lastDayNextMonth));
};
exports.getNextMonthlyOccurrence = getNextMonthlyOccurrence;
/**
 * Returns the next occurrence of a monthly recurring event for a given dayOfMonth,
 * using the provided IANA timezone to determine "today."
 *
 * Uses inclusive comparison (>=): if today IS the target day, returns today.
 * Used by bill pay so same-day recurring payments execute immediately.
 */
const getNextMonthlyOccurrenceInTimezone = (dayOfMonth, timezone) => {
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: timezone }); // YYYY-MM-DD
    const [year, monthOneIndexed, day] = todayStr.split('-').map(Number);
    const month = monthOneIndexed - 1; // Date constructor uses 0-indexed months
    const clampedDay = clampDayToMonth(dayOfMonth, year, month);
    if (clampedDay >= day) {
        return new Date(year, month, clampedDay);
    }
    const nextMonthDate = new Date(year, month + 1, 1);
    return new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), clampDayToMonth(dayOfMonth, nextMonthDate.getFullYear(), nextMonthDate.getMonth()));
};
exports.getNextMonthlyOccurrenceInTimezone = getNextMonthlyOccurrenceInTimezone;
/**
 * Returns the occurrence of a monthly recurring event in the month following the given date.
 * Clamps to the last day of the month for short months.
 */
const getFollowingMonthOccurrence = (fromDate, dayOfMonth) => {
    const nextMonth = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 1);
    return new Date(nextMonth.getFullYear(), nextMonth.getMonth(), clampDayToMonth(dayOfMonth, nextMonth.getFullYear(), nextMonth.getMonth()));
};
exports.getFollowingMonthOccurrence = getFollowingMonthOccurrence;
/**
 * Returns which dayOfMonth values should be processed for a given date.
 *
 * On the last day of a short month (< 31 days), we also process configs
 * for the days that don't exist in that month so they aren't skipped.
 *   - Feb 28 (non-leap) -> [28, 29, 30, 31]
 *   - Feb 29 (leap)     -> [29, 30, 31]
 *   - Apr 30             -> [30, 31]
 *   - Jan 31             -> [31]   (31 exists, normal processing)
 *   - Mar 15             -> [15]
 */
const getDaysOfMonthToProcess = (date) => {
    const dayOfMonth = date.getDate();
    // Check if tomorrow would roll into the next month (i.e. today is the last day)
    const tomorrow = new Date(date);
    tomorrow.setDate(dayOfMonth + 1);
    const isLastDayOfMonth = tomorrow.getMonth() !== date.getMonth();
    if (isLastDayOfMonth && dayOfMonth < MAX_DAY_OF_MONTH) {
        // Include today through 31 so configs for non-existent days still fire
        const days = [];
        for (let d = dayOfMonth; d <= MAX_DAY_OF_MONTH; d++) {
            days.push(d);
        }
        return days;
    }
    return [dayOfMonth];
};
exports.getDaysOfMonthToProcess = getDaysOfMonthToProcess;
